import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    CheckBox,
    DropUploader,
    Icon,
    Input,
    Spacer,
    SubTitle,
    Text,
    Theme, ThirdTitle,
    Title,
    Toggle
} from "@dspworkplace/ui";
import { CLOTHING_SIZES, csvExtention, SHOW_SIZES, USA_STATES } from "../../../patterns";
import { useForm } from "react-hook-form";
import Loading, { useLoading } from "../../../components/Loading";
import Modal, { useModal } from "../../../components/Modal";
import { Label, Link, VerticalDash } from "../../../components/UI";
import Papa from 'papaparse';
import PayrollImportStore, { PayrollImportProvider } from "./context";
import { getCompanyId, useAuth } from "../../../Auth";
import { useHistory } from "react-router-dom";
import Skeleton from "../../../components/Skeleton";
import { DataGrid } from "../../../components/Grid";
import './styles.css';
import { getSelectedStationId, pluralize } from "../../../Utilities";
import { Combobox } from "../../../components/Form/Combobox";
import { useSeatsInformation } from "../../../api/useSubscriptionsApi";
import { RecommendedFormatter, RequiredFormatter, SimpleFormatter } from "./formatters";
import { DateEditor, DropdownEditor, EmailEditor, SmallTextEditor } from "./editor";
import { dialog, loadingDialog } from "../../../components/Dialog";
import apiFactory from "../../../api/factory";
import Tooltip from "../../../components/Tooltip";
import TooltipPortal from "../../../components/Tooltip/tooltip";
import { useLocation } from "react-router-dom";

const PayrollImport = () => {
    const history = useHistory();
    const location = useLocation();
    const [isLoading, setLoading] = useLoading();
    const [error, setError] = useState(0);
    const [state, dispatch] = useContext(PayrollImportStore);
    const [csvVisible, toggleCsv] = useModal(false);
    const [changeManagerVisible, toggleChangeManager] = useModal(false);
    const [editFieldsVisible, toggleEditFields] = useModal(false);

    const type = location.state?.type ?? '';
    
    const { call, cancel } = apiFactory();
    let [{ availableSeats, reservedSeats, flexibleSeats }, refreshSeatsInformation] = useSeatsInformation();

    const fetchData = async () => {
        const params = {
            "actions": {
                "response": {
                    "Role": {
                        "findBy": {
                            "get": "roles",
                            "criteria": {
                                "company": getCompanyId()
                            },
                            "includes": [
                                "id",
                                "roleName",
                                "name"
                            ]
                        }
                    },
                    "EmploymentStatus": {
                        "findBy": {
                            "get": "employmentStatus",
                            "criteria": {
                                "companyId": getCompanyId()
                            },
                            "includes": {
                                "0": "id",
                                "1": "name"
                            }
                        }
                    },
                    "Station": {
                        "findBy": {
                            "criteria": {
                                "company": getCompanyId(),
                                "isArchive": false
                            },
                            "get": "stations",
                            "id": getCompanyId(),
                            "includes": {
                                "0": "id",
                                "1": "name",
                                "2": "code",
                            }
                        }
                    },
                    "User": {
                        "custom": {
                            "functionName": "findManagers",
                            "get": "users",
                            "criteria": {
                                "company": getCompanyId()
                            }
                        }
                    },
                    "Skill": {
                        "findBy": {
                            "criteria": { company: getCompanyId() },
                            "get": "skills",
                            "orderBy": {
                                name: "ASC"
                            },
                            "includes": {
                                0: "id",
                                1: "name"
                            },
                            "excludes": [
                                "company",
                                "skill",
                                "users",
                                "parent",
                                "childrens",
                                "driverRoutes",
                                "payRate",
                                "skillRates",
                                "shifts",
                                "drivers",
                                "vehicles"
                            ]
                        }
                    }
                }
            }
        }
        const response = await call(params);
        dispatch({
            type: "SET_OPTIONS",
            payload: {
                station: response.data.data.stations.map((s) => {
                    return { value: s.id, name: s.name.toString(), label: s.code.toString() }
                }),
                employmentStatus: response.data.data.employmentStatus,
                permissionLevel: response.data.data.roles,
                skills: response.data.data.skills,
                managers: response.data.data.users.map((user) => {
                    let newValue = (user?.driverPayrollId?.toString() ?? '').trim();
                    const payrollAccount = (user?.payrollAccountId?.toString() ?? '').trim();

                    newValue = payrollAccount ? newValue + payrollAccount :  user.id.toString();
                    
                    return {
                        name: user.firstName.toString() + ((user.lastName) ? ' ' + user.lastName.toString() : ''),
                        label: user.firstName.toString() + ((user.lastName) ? ' ' + user.lastName.toString() : ''),
                        value: newValue,
                        user_id: user.id.toString(),
                        payroll_account_id: user?.payrollAccountId ?? null,
                        driver_payroll_id: user?.driverPayrollId ?? null,
                        isOwner: user?.title?.toLowerCase() === 'owner'
                    }
                })
            }
        });
    };

    const fetchPayrollData = async () => {
        setLoading(true);
        const params = {
            integration_type: type.toUpperCase()
        }
        call(params,'/api/v2/payroll_account/getDrivers').then(response => {
            dispatch({
                type: "SET_FETCH_PAYROLL_DATA",
                payload: {
                    data: response.data,
                    columns: columns,
                    selectedStation: getSelectedStationId()
                },
            });
            setLoading(false);
        }).catch(() => {
            setError(1);
            setLoading(false);
        });
    }

    const doImport = async () => {
        let closeLoading = loadingDialog();
        await refreshSeatsInformation().then(refreshed => {
            reservedSeats = refreshed.reservedSeats;
            availableSeats = refreshed.availableSeats;
            flexibleSeats = refreshed.flexibleSeats;
        });
        closeLoading();

        const shouldBeImported = state.data.filter(d => d.import);
        const newEmployeesCount = shouldBeImported.length;
        const exceededCount = newEmployeesCount - availableSeats;

        if (!flexibleSeats && exceededCount > 0) {
            const { action } = await dialog({
                modal: {
                    width: '600px'
                },
                align: {
                    content: 'left'
                },
                icon: <Icon.Warning size={'60px'} color={Theme.colors.warnings.text} />,
                title: "You have exceeded your seat limits",
                text: `
                    Your account seat limit is currently set to ${reservedSeats}.<br/>
                    You are attempting to import ${newEmployeesCount} new ${pluralize(newEmployeesCount, 'employee')}, 
                    which exceeds your seat count by ${exceededCount}.<br/>
                    To modify your seat count, you need to update the seat count or switch to our Flexible Seats add-on. 
                    Click the button below to make the change.  
                `,
                buttons: [
                    {
                        type: 'cancel',
                        Type: 'submit',
                        text: 'Review my import',
                        action: 'cancel',
                    },
                    {
                        type: 'primary',
                        text: 'Upgrade my plan',
                        action: 'redirect',
                    }
                ]
            });

            if (action === 'cancel')
                return;

            if (action === 'redirect')
                return history.push('/settings/package');
        }

        setLoading(true);

        dispatch({
            type: 'SET_IMPORTING',
            payload: shouldBeImported
        })

        const convertMDYtoYMD = (mdy) => {
            const [month, day, year] = mdy.split('/').map(Number);
            const mm = String(month).padStart(2, '0');
            const dd = String(day).padStart(2, '0');
            return `${year}-${mm}-${dd}`;
        }

        const managers = state.options.managers.reduce((acc, curr) => {
            acc[curr.value] = curr.user_id;
            return acc;
        }, {});

        const owner = state.options.managers.find(m => m?.isOwner)



        for (let i = 0; i < shouldBeImported.length; i++) {
            if(shouldBeImported[i].dateOfBirth){
                shouldBeImported[i].birthDate = convertMDYtoYMD(shouldBeImported[i].dateOfBirth)
                delete shouldBeImported[i].birthDate;
            }
            if(shouldBeImported[i].hireDate){
                shouldBeImported[i].hireDate = convertMDYtoYMD(shouldBeImported[i].hireDate)
            }
            if(shouldBeImported[i].licenseExpDate){
                shouldBeImported[i].licenseExpDate = convertMDYtoYMD(shouldBeImported[i].licenseExpDate)
            }
            if(shouldBeImported[i].dotMedialExpDate){
                shouldBeImported[i].dotMedialExpDate = convertMDYtoYMD(shouldBeImported[i].dotMedialExpDate)
            }
            if(shouldBeImported[i].phone){
                shouldBeImported[i].personalPhone = shouldBeImported[i].phone
                delete shouldBeImported[i].phone
            }

            if(managers[shouldBeImported[i].managerId]){
                shouldBeImported[i].managerId = parseInt(managers[shouldBeImported[i].managerId])
            }else{
                shouldBeImported[i].managerId = parseInt(owner.user_id)
            }

        
            await call({
                employees: [shouldBeImported[i]],
                payroll: type
            }, "/api/v2/payroll_account/importDrivers").then(r => {
                if (!r?.data?.success) {
                    shouldBeImported[i].importingStatus = 'error';
                    shouldBeImported[i].importingError = r?.data?.errors ?? r?.data?.message;
                } else
                    shouldBeImported[i].importingStatus = 'success';

                dispatch({
                    type: 'SET_IMPORTING',
                    payload: shouldBeImported
                })
            })
        }
    };

    useEffect(() => {
        fetchData().then(() => {
            fetchPayrollData();
        });

        return () => cancel();
    }, []);

    const setSortBy = sortBy => {
        dispatch({
            type: "SET_SORT_BY",
            payload: sortBy
        });
    };

    const onRowChange = () => {
        dispatch({
            type: "SET_VALIDATION",
        });
    };

    const onSearch = (search) => {
        dispatch({
            type: "SEARCH",
            payload: search
        });
    };

    const RequiredHeader = useCallback((label, key) => {
        const count = state?.validation?.required?.[key]?.count;

        if (!count)
            return label;

        return (
            <span
                style={{
                    display: 'flex',
                    alignItems: "end",
                    gap: 4,
                    color: Theme.colors.error.text,
                }}
                title={`${count} ${pluralize(count, 'record')} ${pluralize(count, 'is', 'are')} missing this field`}
            >
                <Icon.Warning size={'16px'} color={Theme.colors.error.text} />
                <span>{label}</span>
            </span>
        )
    }, [state.validation]);

    const RecommendedHeader = useCallback((label, key) => {
        const count = state?.validation?.recommended?.[key]?.count;

        if (!count)
            return label;

        return (
            <span
                style={{
                    display: 'flex',
                    alignItems: "end",
                    gap: 4,
                    color: Theme.colors.warnings.text,
                }}
                title={`${count} ${pluralize(count, 'record')} ${pluralize(count, 'is', 'are')} missing this field`}
            >
                <Icon.Warning size={'16px'} color={Theme.colors.warnings.text} />
                <span>{label}</span>
            </span>
        )
    }, [state.validation]);

    const ChangeManagerDialog = () => {
        const [formValues, setFormValues] = useState({
            managerId: null,
        });

        const onSubmit = e => {
            e.preventDefault();



            dispatch({
                type: "SET_BULK_SELECTED_FIELD_DATA_UPDATE",
                payload: {
                    fields: formValues
                }
            });

            toggleChangeManager();
        };

        return (
            <Modal
                title={"Change Manager"}
                width="340px"
                visible={changeManagerVisible}
                setVisible={() => {
                    toggleChangeManager();
                }}
                zindex="40"
            >
                <form onSubmit={onSubmit}>
                    <Spacer bottom={5}>
                        <Combobox
                            name='manager'
                            label='Manager'
                            placeholder='-'
                            isAdd={false}
                            options={state.options.managers}
                            onChange={(value) => setFormValues(prev => ({ ...prev, managerId: value?.value ?? null }))}
                        />
                    </Spacer>
                    <Button type="primary">
                        Save
                    </Button>
                </form>
            </Modal>
        );
    };

    const EditFieldsDialog = () => {
        const [formValues, setFormValues] = useState({
            stationId: null,
            employmentStatusId: null,
            permissionLevelId: null,
            skillId: null,
        });

        const onSubmit = e => {
            e.preventDefault();

            dispatch({
                type: "SET_BULK_SELECTED_FIELD_DATA_UPDATE",
                payload: {
                    fields: formValues
                }
            });

            toggleEditFields();
        };

        return (
            <Modal
                title={"Edit Fields"}
                width="340px"
                visible={editFieldsVisible}
                setVisible={() => {
                    toggleEditFields();
                }}
                zindex="40"
            >
                <form onSubmit={onSubmit}>
                    <Combobox
                        name='station'
                        label='Station'
                        placeholder='-'
                        isAdd={false}
                        options={state.options.station}
                        onChange={(value) => setFormValues(prev => ({ ...prev, stationId: value?.value ?? null }))}
                    />
                    <Spacer top={3} />
                    <Combobox
                        name='employmentStatus'
                        label='Employment Status'
                        placeholder='-'
                        isAdd={false}
                        options={(state.options.employmentStatus ?? []).map((emp) => {
                            return { label: emp.name.toString(), value: emp.id }
                        })}
                        onChange={(value) => setFormValues(prev => ({ ...prev, employmentStatusId: value?.value ?? null }))}
                    />
                    <Spacer top={3} />
                    <Combobox
                        name='permissionLevel'
                        label='Permission Level'
                        placeholder='-'
                        isAdd={false}
                        options={(state.options.permissionLevel ?? []).map((r) => {
                            return { label: r.roleName.toString(), value: r.id }
                        })}
                        onChange={(value) => setFormValues(prev => ({ ...prev, permissionLevelId: value?.value ?? null }))}
                    />
                    <Spacer top={3} />
                    <Combobox
                        name='skill'
                        label='Skill'
                        placeholder='-'
                        isAdd={false}
                        options={(state.options.skills ?? []).map((s) => {
                            return { label: s.name.toString(), value: s.id }
                        })}
                        onChange={(value) => setFormValues(prev => ({ ...prev, skillId: value?.value ?? null }))}
                    />
                    <Spacer top={5} />
                    <Button type="primary">
                        Save
                    </Button>
                </form>
            </Modal>
        );
    };

    const UploadAssociatesDialog = () => {
        const { register, handleSubmit, errors, getValues } = useForm({ defaultValues: {} });
        const { csvFile } = getValues();

        const onSubmit = async data => {
            const content = await new Promise((resolve, reject) => {
                Papa.parse(data.csvFile[0], {
                    header: true, // Assuming the CSV has a header row
                    complete: resolve,
                    error: reject
                });
            })

            dispatch({
                type: 'SET_CSV_DATA',
                payload: content
            })
            toggleCsv();
        };

        const errorMessages = {
            csvFile: {
                required: "You need to provide an csv",
                pattern: "select file format is not valid"
            },
        };

        return (
            <Modal
                title={"Upload Associates Data"}
                width="400px"
                visible={csvVisible}
                setVisible={() => {
                    toggleCsv();
                }}
                zindex="40"
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Text>
                        Go to Cortex and download the .csv file with your associates data. If you have more than one
                        station, you might need to download several files, one for each.
                    </Text>
                    <Spacer top={5} />
                    <Link href='https://logistics.amazon.com/workforce?pageId=da_console_associates&station=ALL&tabId=da-console-associates-tab'>
                        Open Cortex "My Associates" page
                    </Link>
                    <Spacer top={5} />
                    <DropUploader
                        name="csvFile"
                        label={'Associates Data CSV'}
                        ref={register({
                            required: true,
                            pattern: csvExtention
                        })}
                        accept={'text/csv'}
                        error={errors.csvFile && errorMessages.csvFile[errors.csvFile.type]}
                        valid={!errors.csvFile && csvFile && csvFile.length > 0}
                    />
                    {errors.csvFile &&
                        <Spacer top={1}>
                            <Label
                                style={{
                                    color: Theme.colors.error.text,
                                    fontSize: Theme.font.small.size,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                <Icon.TimesNarrow size={'8px'} color={Theme.colors.error.text} />
                                {errors.csvFile && errorMessages.csvFile[errors.csvFile.type]}
                            </Label>
                        </Spacer>
                    }
                    <Spacer top={5} />
                    <Button type="primary">
                        Import
                    </Button>
                </form>
            </Modal>
        );
    };

    const columns = useMemo(() => {
        let c = [];

        if (state.bulkEdit)
            c.push({
                key: "select",
                name: <Spacer style={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                    <CheckBox
                        options={[{ label: "", value: false }]}
                        onClick={(e) => {
                            dispatch({
                                type: "SET_BULK_FIELD_DATA_UPDATE",
                                payload: {
                                    field: 'select',
                                    newValue: e.target.checked
                                }
                            });
                        }}
                    />
                </Spacer>,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                froze: true,
                formatter({ row }) {
                    return <Spacer left={2} style={{ height: '100%' }} onClick={() => {
                        row.select = !row.select;
                        dispatch({
                            type: "SET_VALIDATION",
                        })
                    }}>
                        <CheckBox
                            defaultValues={[row.select]}
                            options={[{ label: "", value: true }]}
                        />
                    </Spacer>;
                },
            });

        c = c.concat([
            // {
            //     key: "payrollId",
            //     name: "Payroll Account",
            //     minWidth:100,
            //     froze: true,
            // },
            {
                key: "import",
                name: <Spacer style={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                    <Toggle
                        singleText=''
                        on={false}
                        onChange={(e) => {
                            dispatch({
                                type: "SET_BULK_FIELD_DATA_UPDATE",
                                payload: {
                                    field: 'import',
                                    newValue: e.target.checked
                                }
                            });
                        }}
                    />
                </Spacer>,
                width: 56,
                minWidth: 56,
                maxWidth: 56,
                froze: true,
                editable: false,
                formatter({ row }) {
                    return <Spacer onClick={() => {
                        row.import = !row.import;
                        dispatch({
                            type: "SET_VALIDATION",
                        })
                    }} left={2} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Toggle
                            singleText={''}
                            on={row.import}
                        />
                    </Spacer>;
                }
            },
            {
                key: "name",
                name: RequiredHeader('Name', 'name'),
                width: 200,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: SmallTextEditor,
                _required: true,
            },
            {
                key: "transporterId",
                name: RecommendedHeader("Transporter ID", 'transporterId'),
                width: 164,
                sortable: true,
                formatter: RecommendedFormatter,
                editable: true,
                editor: SmallTextEditor,
                _recommended: true,
            },
            {
                key: "dateOfBirth",
                name: 'Date of Birth',
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: DateEditor,
            },
            {
                key: "phone",
                name: RequiredHeader('Phone', 'phone'),
                width: 160,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: SmallTextEditor,
                _required: true,
            },
            {
                key: "email",
                name: RequiredHeader("Primary Email", 'email'),
                width: 200,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: EmailEditor,
                _required: true,
            },
            {
                key: "secondaryEmail",
                name: "Secondary Email",
                width: 200,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: EmailEditor,
            },
            {
                key: "stationId",
                name: RequiredHeader("Station", 'stationId'),
                width: 160,
                formatter: RequiredFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: state.options.station ?? []
                },
                _required: true,
            },
            {
                key: "managerId",
                name: RequiredHeader("Manager", 'managerId'),
                width: 120,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: state.options.managers ?? [],
                    default: state.options.managers?.find(m => m?.isOwner)
                },
                _required: true,
            },
            {
                key: "hireDate",
                name: RequiredHeader("Hiring Date", 'hiringDate'),
                width: 120,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: DateEditor,
                _required: true,
            },
            {
                key: "employmentStatusId",
                name: RequiredHeader("Employment Status", 'employmentStatusId'),
                width: 160,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: (state.options.employmentStatus ?? []).map((emp) => {
                        return { label: emp.name.toString(), value: emp.id }
                    })
                },
                _required: true,
            },
            {
                key: "permissionLevelId",
                name: RequiredHeader("Permission Level", 'permissionLevelId'),
                width: 200,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: (state.options.permissionLevel ?? []).map((r) => {
                        return { label: r.roleName.toString(), value: r.id }
                    })
                },
                _required: true,
            },
            {
                key: "skillId",
                name: RequiredHeader("Skill", 'skillId'),
                width: 160,
                sortable: true,
                formatter: RequiredFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: (state.options.skills ?? []).map((s) => {
                        return { label: s.name.toString(), value: s.id }
                    })
                },
                _required: true,
            },
            {
                key: "licenseNumber",
                name: RecommendedHeader("License Number", 'licenseNumber'),
                width: 160,
                sortable: true,
                formatter: RecommendedFormatter,
                editable: true,
                editor: SmallTextEditor,
                _recommended: true,
            },
            {
                key: "licenseState",
                name: RecommendedHeader("License State", 'licenseState'),
                width: 160,
                sortable: true,
                formatter: RecommendedFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: USA_STATES
                },
                _recommended: true,
            },
            {
                key: "licenseExpDate",
                name: RecommendedHeader("License Expiration Date", 'licenseExpDate'),
                width: 160,
                sortable: true,
                formatter: RecommendedFormatter,
                editable: true,
                editor: DateEditor,
                _recommended: true,
            },
            {
                key: "gasPin",
                name: "Gas Pin",
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: SmallTextEditor,
            },
            {
                key: "dotMedialExpDate",
                name: "DOT Medial Exp. Date",
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: DateEditor,
            },
            {
                key: "shirtSize",
                name: "Shirt Size",
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: CLOTHING_SIZES
                }
            },
            {
                key: "pantSize",
                name: "Pant Size",
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: CLOTHING_SIZES
                }
            },
            {
                key: "shoeSize",
                name: "Shoe Size",
                width: 120,
                sortable: true,
                formatter: SimpleFormatter,
                editable: true,
                editor: DropdownEditor,
                _dropdown: {
                    options: SHOW_SIZES
                }
            },
        ]);

        return c;
    }, [state.bulkEdit, state.options, state.validation])

    let tooltipImportContent;

    if (state?.validation?.canImport === undefined) {
        tooltipImportContent = <Text>Fetching...</Text>;
    } else if (state?.validation?.importingCount === 0) {
        tooltipImportContent = <Text>Please select at least one employee.</Text>;
    } else if (!state?.validation?.canImport) {
        tooltipImportContent = (
            <>
                <Text>
                    {`Your import selection has ${state?.validation?.numberEmployeesWithRequired} employees with required fields missing.`}
                </Text>
                <Text>Please add the missing information or remove them from selection.</Text>
            </>
        );
    } else {
        tooltipImportContent = <Text>Import Employees</Text>;
    }

    return (
        <Spacer all={5} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <header style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Title>Import Employees from {type}</Title>
                <div style={{
                    display: state.bulkEdit || state.importingStatus ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12
                }}>
                    {!error && (
                        isLoading ? (
                            <Skeleton
                                height={20}
                                width={220}
                            />
                        ) : (
                            <Text style={{
                                fontStyle: Theme.font.small.size,
                                color: Theme.colors.neutrals.medium
                            }}>
                                Importing {state?.validation?.importingCount} from {state.data.length} employees
                            </Text>
                        )
                    )}

                    <VerticalDash />

                    <Input
                        name="search"
                        placeholder="Filter drivers by name, email, phone number, etc"
                        onChange={(e) => onSearch(e.target.value)}
                        style={{ "marginLeft": "auto" }}
                        disabled={isLoading || !state.rawData.length || error}
                    >
                        <Icon.Search color={Theme.colors.info.border} size={"24px"} />
                    </Input>

                    <Button
                        disabled={isLoading || !state.rawData.length || error}
                        onClick={() => {
                            dispatch({
                                type: "SET_BULK_EDIT",
                                payload: true
                            });
                        }}
                    >Batch Edit</Button>

                    <Button
                        type='cancel'
                        onClick={() => {
                            history.push("/organization/employees")
                        }}
                    >Cancel</Button>

                    <Tooltip
                        verticalPosition={'center'}
                        horizontalPosition={'left'}
                        content={tooltipImportContent}
                    >
                        <Button
                            type="primary"
                            onClick={doImport}
                            disabled={!state?.validation?.canImport}
                        >
                            Import
                        </Button>
                    </Tooltip>
                </div>

                {state.bulkEdit &&
                    <Button
                        type="primary"
                        onClick={() => {
                            dispatch({
                                type: "SET_BULK_EDIT",
                                payload: false
                            });
                        }}
                    >Done</Button>
                }

                {state.importingStatus &&
                    <div style={{
                        display: "flex",
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                    }}>
                        {/*
                        <Button
                            type='cancel'
                            onClick={() => {
                                cancel();
                                dispatch({
                                    type: 'STOP_IMPORTING'
                                })
                            }}
                        >Stop</Button>
                        */}
                        <Button
                            type={'primary'}
                            Type={'button'}
                            onClick={() => history.push('/organization/employees')}
                            style={{
                                display: state.importingStatus.progress === 100 ? 'block' : 'none'
                            }}
                        >Done</Button>
                    </div>
                }
            </header>

            {state.bulkEdit &&
                <Spacer top={5}
                    style={{ display: "flex", gap: 8, color: Theme.colors.neutrals.medium, alignItems: "center" }}>
                    <Label>With selected:</Label>
                    <Button
                        size={'small'}
                        onClick={() => {
                            toggleChangeManager(true);
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            Change Manager <Icon.Person size={'16px'} />
                        </span>
                    </Button>
                    <Button
                        size={'small'}
                        onClick={() => {
                            dispatch({
                                type: "SET_BULK_SELECTED_FIELD_DATA_UPDATE",
                                payload: {
                                    fields: { dateOfBirth: false },
                                }
                            });
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            Clear Date of Birth <Icon.Trash size={'16px'} />
                        </span>
                    </Button>
                    <Button
                        size={'small'}
                        onClick={() => {
                            toggleEditFields(true);
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            Edit Fields<Icon.Edit size={'16px'} />
                        </span>
                    </Button>
                    <Button
                        size={'small'}
                        onClick={() => {
                            dispatch({
                                type: "SET_BULK_SELECTED_FIELD_DATA_UPDATE",
                                payload: {
                                    fields: { import: true },
                                }
                            });
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            Select To Import <Icon.Check size={'16px'} />
                        </span>
                    </Button>
                    <Button
                        size={'small'}
                        onClick={() => {
                            dispatch({
                                type: "SET_BULK_SELECTED_FIELD_DATA_UPDATE",
                                payload: {
                                    fields: { import: false },
                                }
                            });
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            Do not Import <Icon.Times size={'16px'} />
                        </span>
                    </Button>
                </Spacer>
            }

            {!state.hasAssociatesData && !isLoading && state.rawData.length > 0 && !state.bulkEdit && <Spacer top={5}>
                <Alert.Info
                    useIcon={true}
                    title={"Improve your import by merging your driver's information with data from AMZ"}
                    content={<>
                        <Spacer bottom={2}>
                            By uploading your associates data .csv file, we can automatically fill several fields for
                            you drivers, such as Transporter ID and Skills
                        </Spacer>
                        <Button
                            size='small'
                            type="primary"
                            onClick={() => {
                                toggleCsv(true);
                            }}
                        >Upload Associates Data</Button>
                    </>}
                />
            </Spacer>}

            {isLoading && !state.rawData.length ? (
                <Spacer top={50} style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <Loading width={40} />
                    <Spacer bottom={5} />
                    <SubTitle>Fetching {type} workers...</SubTitle>
                    <Spacer bottom={3} />
                    <Text>This may take a few moments</Text>
                </Spacer>
            ) : null}

            {!isLoading && error === 1 && (
                <Spacer top={50} style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <Icon.Warning size={'40px'} color={Theme.colors.error.text} />
                    <Spacer bottom={5} />
                    <SubTitle style={{ color: Theme.colors.error.text }}>{type} Workers data failed to load</SubTitle>
                    <Spacer bottom={3} />
                    <Text>Please review your <Link to='/settings/payroll-settings'>Payroll Integration configuration</Link> and try
                        again.</Text>
                    {type === 'ADP' && <Text>Make sure your dspworkplace ADP marketplace app is active.</Text>}
                    <Spacer top={5}>
                        <Button type='cancel'
                            onClick={() => history.push('/organization/employees')}>Cancel</Button>
                        <Spacer inline right={5}></Spacer>
                        <Button onClick={() => history.push('/settings/payroll-settings')}>{type} Integration Configuration</Button>
                    </Spacer>
                </Spacer>
            )}

            {!isLoading && state.rawData.length > 0 &&
                <Spacer style={{ flexGrow: 1 }}>
                    <DataGrid
                        isStation={false}
                        rowHeight={32}
                        headerRowHeight={44}
                        status={'ready'}
                        columns={columns}
                        rows={state.sorted ?? state.data}
                        setCustomRowClass={row => {
                            return !row?.import ? 'not-importing' : '';
                        }}
                        onSort={setSortBy}
                        onEditorChange={onRowChange}
                    />
                </Spacer>
            }

            {isLoading && state.importing &&
                <>
                    <Spacer top={5} style={{
                        display: "flex",
                        flexDirection: "column",
                    }}>
                        <Spacer bottom={2}>
                            <ThirdTitle>
                                Importing workers
                                <small style={{
                                    display: "inline-block",
                                    paddingLeft: 8,
                                    color: Theme.colors.neutrals.medium
                                }}>{state.importingStatus.progress}%</small>
                            </ThirdTitle>
                        </Spacer>
                        <div style={{
                            flexGrow: 1,
                            height: 12,
                            borderRadius: Theme.defaultRadius,
                            background: Theme.colors.info.bg,
                        }}>
                            <div style={{
                                width: `${state.importingStatus.progress}%`,
                                height: 12,
                                borderRadius: Theme.defaultRadius,
                                background: state.importingStatus.progress === 100 ? Theme.colors.success.text : Theme.colors.secondary,
                                transition: 'all .2s'
                            }}></div>
                        </div>
                    </Spacer>
                    <Spacer style={{ flexGrow: 1 }}>
                        <DataGrid
                            isStation={false}
                            rowHeight={32}
                            headerRowHeight={44}
                            status={'ready'}
                            columns={[
                                {
                                    key: 'importingStatus',
                                    name: 'Status',
                                    formatter({ row }) {
                                        return <Spacer left={1} style={{
                                            height: '100%',
                                            display: 'flex',
                                            gap: 8,
                                            alignItems: 'center',
                                        }}>
                                            {(() => {
                                                
                                                switch (row.importingStatus) {
                                                    case undefined:
                                                        return <Icon.Clock size={'20px'} />

                                                    case 'success':
                                                        return <Icon.Check size={'20px'}
                                                            color={Theme.colors.success.text} />

                                                    case 'error':
                                                        return <>
                                                            <Icon.Times
                                                                size={'20px'}
                                                                color={Theme.colors.error.text}
                                                                style={{ flexShrink: 0 }}
                                                            />
                                                            <Text style={{ whiteSpace: 'normal' }}>{ErrorDisplay(row.importingError)}</Text>
                                                        </>
                                                }
                                            })()}
                                        </Spacer>
                                    }
                                },
                                {
                                    key: 'name',
                                    name: 'Name',
                                    formatter: SimpleFormatter
                                },
                                {
                                    key: 'email',
                                    name: 'Email',
                                    formatter: SimpleFormatter
                                },
                                {
                                    key: 'payrollDriverId',
                                    name: 'Payroll OID',
                                    formatter: SimpleFormatter
                                },
                            ]}
                            rows={state.importing}
                        />
                    </Spacer>
                </>
            }

            <UploadAssociatesDialog />
            <ChangeManagerDialog />
            <EditFieldsDialog />
        </Spacer>
    );
};

function ErrorDisplay(error) {
  if(typeof error === 'string'){
    return error
  }  

  return (
    <TooltipPortal 
        content={
        error.map((err, i) => (
            <Text key={i}>
              {err.message}
              {'\n'}
            </Text>
          ))
    }>
        <Text>Multiple errors</Text>
    </TooltipPortal>
  )
}

const App = () => {
    return (
        <PayrollImportProvider>
            <PayrollImport />
        </PayrollImportProvider>
    );
};

export default App;